import { createApp } from 'vue';
import {
    createRouter, createWebHistory, RouteRecordRaw,
} from 'vue-router';
import '@/styles/bootstrap.scss';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/default.css';
import '@fontsource/titillium-web';
import App from '@/App.vue';
import NotFoundPage from '@/components/pages/NotFoundPage.vue';
import { getStromecek } from '@/config/web_config';
import VuePictureSwipe from 'vue3-picture-swipe';
import '@/styles/styles.scss';
import { generateRoutesFromMenu, generateMenuFrom } from '@/menu';
import { store } from './store';
import { katexDirective } from '@/katex-run';
import { hljsDirective } from './hljs-run';
import webService from '@/api/WebService';
import authService from './api/AuthService';
import { createBootstrap } from 'bootstrap-vue-next';

(async() => {
    try {
        let [[stromecek, extraRoutes], landingTitle] = await Promise.all([
            getStromecek(),
            webService.getCurrentLandingTitle(),
            authService.refreshLogin(),
        ]);

        // If the user is an organiser, recalculate the routes, since they can see future contests.
        if (store.state.isOrg)
            [stromecek, extraRoutes] = await getStromecek();

        const menu = generateMenuFrom(stromecek);

        // craate App
        const app = createApp(App, { menuStromecek: menu, landingTitle });

        // initialize Vuex Store
        app.use(store);

        // initialize other libraries
        app.component('VuePictureSwipe', VuePictureSwipe);
        app.directive('katex', katexDirective);
        app.directive('syntaxHighlight', hljsDirective);
        app.use(createBootstrap());

        // initialize Router
        const routes: RouteRecordRaw[] = generateRoutesFromMenu(menu);
        routes.push(...extraRoutes);
        routes.push({ path: '/', redirect: { path: '/uvod/' } });
        routes.push({
            path: '/:catchAll(.*)',
            component: NotFoundPage,
            meta: {
                title: '404 Stránka nenalezena',
                headlineTop: 'Chyba 404',
                headlineBottom: 'Stránka nenalezena',
            },
        });

        const router = createRouter({
            history: createWebHistory(),
            routes,
            scrollBehavior(_to, _from, savedPosition) {
                // Keep back/forward position; otherwise always start at the top
                // (e.g. when switching tasks so the new assignment is readable from the start).
                if (savedPosition) {
                    return savedPosition;
                }
                return { top: 0, left: 0 };
            },
        });

        router.beforeEach((to: any, _: any, next: any) => {
            if (to == null || to.meta == null || to.meta.title == null || to.path.startsWith('/uvod/')) {
                document.title = 'Kasiopea';
            } else if (to.meta.pageTitle) {
                document.title = `${to.meta.pageTitle} – Kasiopea`;
            } else {
                document.title = `${to.meta.title} – Kasiopea`;
            }
            next();
        });

        router.beforeEach((to: any, from: any, next: any) => {
            if (!to.fullPath.endsWith('/')) {
                next(`${to.fullPath}/`);
            } else {
                next();
            }
        });

        app.use(router);

        // mount App
        app.mount('#app');
    } catch (error) {
        console.error(error);
    }
})();
